import { useState, useEffect, FC } from "react";
import ProfileDefault from "../../assets/icons/user.png";
import { useIntl } from "react-intl";
import { Article } from "../../interfaces/Article";
import { format, parseISO } from "date-fns";
import { id as localeId } from "date-fns/locale";
import { enUS } from "date-fns/locale";
import { Helmet } from "react-helmet-async";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay } from "swiper/modules";

interface Props {
  id: string;
  locale?: string;
}
const PostContent: FC<Props> = ({ id, locale }) => {
  const API_URL = process.env.REACT_APP_API_URL;
  const intl = useIntl();
  const [post, setPost] = useState<Article | null>(null);
  const [loading, setLoading] = useState(true);
  const dateLocale = locale === "en" ? enUS : localeId;
  const thumbnails = post?.thumbnail || [];

  useEffect(() => {
    if (id) {
      window.scrollTo(0, 0);

      setLoading(true);
      fetch(`${API_URL}article/${id}`)
        .then((res) => res.json())
        .then((json) => {
          setPost(json.data);
          setLoading(false);
        })
        .catch(() => setLoading(false));
    }
  }, [id]);

  useEffect(() => {
    if (!id) return;

    const sessionKey = `viewed_article_${id}`;
    const hasViewed = sessionStorage.getItem(sessionKey);

    if (!hasViewed) {
      const timer = setTimeout(() => {
        fetch(`${API_URL}article/${id}/view`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
        })
          .then(() => {
            sessionStorage.setItem(sessionKey, "true");
          })
          .catch((err) => console.error("Failed to update view count", err));
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [id]);

  if (loading)
    return (
      <div className="text-center py-5" style={{ minHeight: "50vh" }}>
        Loading...
      </div>
    );
  if (!post && !loading)
    return (
      <div className="text-center py-5" style={{ minHeight: "50vh" }}>
        {intl.formatMessage({ id: "BLOG.NOT_FOUND" })}
      </div>
    );

  const createDescription = (html: string) => {
    const plainText = html.replace(/<[^>]*>/g, "");
    return plainText.substring(0, 160) + "...";
  };

  const currentUrl = window.location.href;
  const imageUrl =
    post?.thumbnail && post?.thumbnail.length > 0 ? post.thumbnail[0] : "";

  const formattedDate = post?.createdAt
    ? format(parseISO(post?.createdAt), "dd MMMM yyyy", { locale: dateLocale })
    : "";

  return (
    <div className="article-container">
      {/* SEO */}
      {post && (
        <Helmet>
          <title>{post.title} | Wooblazz</title>
          <meta name="description" content={createDescription(post.content)} />

          <meta property="og:title" content={post.title} />
          <meta
            property="og:description"
            content={createDescription(post.content)}
          />
          {imageUrl && <meta property="og:image" content={imageUrl} />}
          <meta property="og:url" content={currentUrl} />
          <meta property="og:type" content="article" />

          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={post.title} />
          <meta
            name="twitter:description"
            content={createDescription(post.content)}
          />
          {imageUrl && <meta name="twitter:image" content={imageUrl} />}
        </Helmet>
      )}

      <div className="container py-5 w-75 w-sm-100">
        {/* Breadcrumb */}
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb justify-content-center">
            <li className="breadcrumb-item">
              <a href="/">{intl.formatMessage({ id: "NAV.HOME" })}</a>
            </li>
            <li className="breadcrumb-item">
              <a href="/blog">{intl.formatMessage({ id: "NAV.BLOG" })}</a>
            </li>
            <li
              className="breadcrumb-item active text-truncate"
              style={{ maxWidth: "80%" }}
              aria-current="page"
            >
              {post?.title}
            </li>
          </ol>
        </nav>

        {/* Profile Author */}
        <div className="profile d-flex justify-content-start inter py-4">
          <div className="profile-img">
            <img
              src={ProfileDefault}
              alt="Profile"
              className="rounded-circle"
              width={50}
              height={50}
              style={{ objectFit: "cover" }}
            />
          </div>
          <p className="ps-3 text-start mb-0">
            <strong>
              <span className="title-author">BY </span>
              Marketing Wooblazz
            </strong>
            <br />
            <span className="title-date text-muted small">{formattedDate}</span>
          </p>
        </div>

        <div className="post-content inter py-4 text-center">
          <h1 className="fw-bold mb-4 px-lg-5">{post?.title}</h1>

          {thumbnails.length > 0 && (
            <div className="mx-auto mb-5" style={{ maxWidth: "750px" }}>
              {thumbnails.length > 1 ? (
                // TAMPILAN CAROUSEL (Jika gambar > 1)
                <Swiper
                  modules={[Navigation, Pagination, Autoplay]}
                  spaceBetween={10}
                  slidesPerView={1}
                  navigation
                  pagination={{ clickable: true }}
                  autoplay={{ delay: 5000 }}
                  loop
                  className="overflow-hidden"
                  style={{
                    aspectRatio: "16 / 9",
                    boxShadow:
                      "0 3px 12px -1px rgba(7, 10, 25, 0.2), 0 22px 27px -20px rgba(7, 10, 25, 0.2)",
                  }}
                >
                  {thumbnails.map((imgUrl, index) => (
                    <SwiperSlide key={index}>
                      <img
                        src={imgUrl}
                        alt={`${post?.title} - ${index + 1}`}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    </SwiperSlide>
                  ))}
                </Swiper>
              ) : (
                // TAMPILAN SINGLE IMAGE (Jika gambar cuma 1)
                <div
                  className="overflow-hidden mx-auto"
                  style={{
                    aspectRatio: "16 / 9",
                    backgroundColor: "#eee",
                    boxShadow:
                      "0 3px 12px -1px rgba(7, 10, 25, 0.2), 0 22px 27px -20px rgba(7, 10, 25, 0.2)",
                  }}
                >
                  <img
                    src={thumbnails[0]}
                    alt={post?.title}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                </div>
              )}
            </div>
          )}

          <div className="text-start px-3 px-lg-5 mt-4">
            <div
              className="article-body-text"
              dangerouslySetInnerHTML={{ __html: post?.content! }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export { PostContent };
